import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useStore } from "../../app/stores/store";
import {
  documentBookingSchema,
  DocumentBookingSchema,
} from "../../lib/schemas/documentBookingSchema";
import { Input } from "@nextui-org/react";
import { ServiceData } from "../../types/service";
import { useNavigate } from "react-router-dom";
import { NationalityData } from "../../types/nationality";
import Select from "react-select";
import { Dropdown } from "primereact/dropdown";
import { separateCamelCase } from "../../lib/utils";
import LoadingSpinner from "../common/LoadingSpinner";
import GooglePayButton from "@google-pay/button-react";
import { toJS } from "mobx";

type Props = {
  service: ServiceData;
};

const DocumentBookingForm = ({ service }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<DocumentBookingSchema>({
    resolver: zodResolver(documentBookingSchema),
    mode: "onTouched",
    defaultValues: {
      adultsNumber: 1,
      childrenNumber: 0,
    },
  });

  const {
    bookingStore: { addBooking },
    nationalityStore: {
      loadNationalities,
      NationalitiesDropdown,
      getNatinality,
    },
    userStore,
  } = useStore();

  const isPartner = userStore.isPartner();
  const isAirportCashier = userStore.isAirportCashier();
  const isCashier = userStore.isCashier();
  const isAdmin = userStore.isAdmin();

  const [selectedNationality, setSelectedNationality] = useState<NationalityData | null>(null);
  const [totalPrice, setTotalPrice] = useState(0);
  const [securityDeposit, setSecurityDeposit] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [includeSecurityDeposit, setIncludeSecurityDeposit] = useState(true);
  const showSecurityDepositOption = isAdmin || isPartner;
const [isSecurityDepositAdjustable, setIsSecurityDepositAdjustable] = useState(false);
const [customSecurityDeposit, setCustomSecurityDeposit] = useState<number | null>(null);

  // Load nationalities when the component mounts
  useEffect(() => {
    loadNationalities();
  }, [loadNationalities]);

  // Recalculate total price whenever any of the relevant values change
useEffect(() => {
  calculateTotalPrice();
}, [
  watch("adultsNumber"),
  watch("childrenNumber"),
  watch("entryType"),
  watch("duration"),
  watch("processTime"),
  selectedNationality,
  includeSecurityDeposit,
  isSecurityDepositAdjustable,
  customSecurityDeposit,
]);


  // Handle nationality change and fetch the details
  const handleNationalityChange = async (selected: string) => {
    const nationality = await getNatinality(selected);
    if (nationality) {
      if (service.name.includes("Tourist Visa")) {
        setSecurityDeposit(Number(nationality.securityDeposit) || 0);
      } else {
        setSecurityDeposit(0); // reset if not a Tourist Visa
      }
      setSelectedNationality(nationality);
      setValue("nationalityId", nationality.id);
    }
    setValue("entryType", "single");
    if (service.name === "Tourist Visa - 2 Month")
      setValue("duration", "60 days");
    else setValue("duration", "30 days");
    setValue("processTime", "Regular - 3-5 working days");
  };

  const calculateTotalPrice = () => {
    if (!selectedNationality) return;

    const adults = Number(watch("adultsNumber"));
    const children = Number(watch("childrenNumber"));
    const entryType = watch("entryType");
    const duration = watch("duration");
    const processTime = watch("processTime");

    let nationalityPrice = 0;
    let totalSecurityDeposit = 0;

    // Always include security deposit for Tourist Visa
    if (service.name.includes("Tourist Visa") && includeSecurityDeposit) {
  if (isSecurityDepositAdjustable) {
totalSecurityDeposit = (customSecurityDeposit ?? 0) * (adults + children);
  } else {
    totalSecurityDeposit =
      Number(selectedNationality.securityDeposit || 0) * (adults + children);
  }
    }

    // =============================
    // 💼 Nationality price logic
    // =============================
    if (service.hasNationalityPrice) {
      if (entryType === "single" && duration === "30 days") {
        nationalityPrice =
          Number(selectedNationality.singlePriceWithMonth) * adults +
          Number(selectedNationality.singlePriceWithMonth) * children;
      } else if (entryType === "single" && duration === "60 days") {
        nationalityPrice =
          Number(selectedNationality.singlePriceWithTwoMonth) * adults +
          Number(selectedNationality.singlePriceWithTwoMonthForChild) * children;

        if (isPartner) {
          nationalityPrice -= 45 * adults; // partner discount
        }
      } else if (entryType === "multiple" && duration === "30 days") {
        nationalityPrice =
          Number(selectedNationality.multiplePriceWithMonth) * adults +
          Number(selectedNationality.multiplePriceWithMonthForChild) * children;
      } else if (entryType === "multiple" && duration === "60 days") {
        nationalityPrice =
          Number(selectedNationality.multiplePriceWithTwoMonth) * adults +
          Number(selectedNationality.multiplePriceWithTwoMonthForChild) * children;
      }

      // ✏️ Adjust nationality price for airport cashier
      if (isAirportCashier && (entryType === "single" && duration === "60 days")) {
        nationalityPrice -= 50 * adults;
      }
      // if (isAirportCashier && (entryType === "single" && duration === "30 days")) {
      //   nationalityPrice -= 50 * children;
      // }
      if (isAirportCashier && (entryType === "single" && duration === "60 days")) {
        nationalityPrice += 50 * children;
      }
    }

    // =============================
    // 🕒 Process time price
    // =============================
    const processTimePrice = isAirportCashier
      ? 0
      : processTime === "Express - 1~2 working days"
        ? Number(service?.expressPrice) || 0
        : Number(service?.regularPrice) || 0;

    // =============================
    // 🧮 Pricing Logic (adjusted)
    // =============================

    const adjustedAdultPrice = isAirportCashier
      ? Number(service.price ?? 0) + Number(service.airportUserPrice ?? 0)
      : isPartner
        ? Number(service.partnerPrice ?? 0) + Number(service.partnerCenterFee ?? 0)
        : Number(service.price ?? 0) + Number(service.centerFee ?? 0);

    const adjustedChildPrice = isAirportCashier
      ? Number(service.childPrice ?? 0) + Number(service.airportUserChildPrice ?? 0)
      : isPartner
        ? Number(service.partnerChildPrice ?? 0) + Number(service.partnerChildCenterFee ?? 0)
        : Number(service.childPrice ?? 0) + Number(service.childCenterFee ?? 0);

    // =============================
    // 💵 Final Calculation
    // =============================
    const totalAdultsPrice = adults * (adjustedAdultPrice + processTimePrice);
    const totalChildrenPrice = children * (adjustedChildPrice + processTimePrice);

    const total =
      nationalityPrice + totalAdultsPrice + totalChildrenPrice + totalSecurityDeposit;

    // =============================
    // 🧾 Debug
    // =============================

    setTotalPrice(total);
    setValue("totalPrice", total);
  };

  const onSubmit = async (data: DocumentBookingSchema) => {
    console.log("data",data);
  // data.includeSecurityDeposit = includeSecurityDeposit; // 👈 Add this

  //     // Include the customSecurityDeposit if included & adjustable
  // if (includeSecurityDeposit) {
  //   data.securityDeposit = isSecurityDepositAdjustable
  //     ? customSecurityDeposit ?? securityDeposit
  //     : securityDeposit;
  // } else {
  //   data.securityDeposit = 0;
  // }


  //   data.totalPrice = totalPrice;
  //   data.serviceId = service.id;
  //   // data.nationalityId = selectedNationality?.id;
  //   setIsLoading(true);

  //   // Submit booking logic here
  //   const result = await addBooking(data);
  //   if (result.status === "success") {
  //     toast.success(t("Booking added successfully"));
  //     navigate(`/listings/booking/upload/${result.data}`);
  //   } else {
  //     toast.error(`${t("Error")}: ${result.error}`);
  //   }
  };


  return isLoading ? (
    <LoadingSpinner />
  ) : (
    <section className="section product-details custom-container">
      <div className="flex justify-center">
        <div className="w-full bg-white p-8 border border-y-slate-950">
          {/* 🔽 Add this */}
          <h3 className="text-2xl md:text-2xl font-extrabold text-left text-black-800 mb-6 tracking-wide border-b-2 pb-2 border-blue-300 font-cairo">
            {service.name}
          </h3>
          {isPartner && (
            <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-xl shadow-sm mb-3">
              <p className="font-semibold">Partner Pricing Applied</p>
              <p className="text-sm">
                You are receiving partner-exclusive pricing.
              </p>
            </div>
          )}
          
{includeSecurityDeposit && service.name.includes("Tourist Visa") && securityDeposit > 0 && (
  <div className="mt-2 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-3 rounded-md shadow-sm text-sm font-sans">
    <strong>Security Deposit Notice:</strong> A security deposit of{" "}
    <span className="font-bold">{isSecurityDepositAdjustable ? (customSecurityDeposit ?? securityDeposit) : securityDeposit} AED</span>{" "}
    will be added based on your nationality selection. This amount is refundable, subject to terms and conditions.
  </div>
)}



          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Input
                  label={t("Customer Name")}
                  {...register("customerName")}
                  isInvalid={!!errors.customerName}
                  errorMessage={errors.customerName?.message}
                  size="sm"
                  className="text-medium md:text-lg font-sans"
                  autoFocus
                />
              </div>

              <div>
                <Input
                  type="tel"
                  label={t("Phone")}
                  {...register("phone")}
                  isInvalid={!!errors.phone}
                  errorMessage={errors.phone?.message}
                  size="sm"
                  className="text-medium md:text-lg font-sans"
                />
              </div>

              <div>
                <Input
                  label={t("Email")}
                  {...register("email")}
                  isInvalid={!!errors.email}
                  errorMessage={errors.email?.message}
                  size="sm"
                  className="text-medium md:text-lg font-sans"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm md:text-lg font-semibold mb-2 font-sans">
                  {t("Choose Nationality")}
                </label>
                <Select
                  className="w-full font-sans z-[100]"
                  options={NationalitiesDropdown?.map((Nationality) => ({
                    label: Nationality.name,
                    value: Nationality.id,
                  }))}
                  onChange={(e) => handleNationalityChange(e!.value)}
                  placeholder={t("Choose Nationality")}
                />
                {errors.nationalityId && (
                  <p className="text-sm text-red-600 mt-1 font-sans">
                    {errors.nationalityId.message}
                  </p>
                )}

                {service.name.includes("Tourist Visa") && showSecurityDepositOption && securityDeposit > 0 && (
  <div className="mt-4 space-y-2">
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        id="includeDeposit"
        checked={includeSecurityDeposit}
        onChange={() => setIncludeSecurityDeposit(!includeSecurityDeposit)}
        className="h-5 w-5 text-blue-600 border-gray-300 rounded"
      />
      <label htmlFor="includeDeposit" className="text-sm text-gray-700 font-sans">
        Include Security Deposit ({securityDeposit} AED)
      </label>
    </div>

    {includeSecurityDeposit && (
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="adjustableDeposit"
          checked={isSecurityDepositAdjustable}
          onChange={() => {
            const newValue = !isSecurityDepositAdjustable;
            setIsSecurityDepositAdjustable(newValue);
            if (!newValue) {
              setCustomSecurityDeposit(null); // Reset if unchecked
            }
          }}
          className="h-5 w-5 text-blue-600 border-gray-300 rounded"
        />
        <label htmlFor="adjustableDeposit" className="text-sm text-gray-700 font-sans">
          Is Security Deposit Adjustable?
        </label>
      </div>
    )}

    {includeSecurityDeposit && isSecurityDepositAdjustable && (
      <div className="flex items-center gap-2 mt-1">
        <label htmlFor="customDeposit" className="text-sm text-gray-700 font-sans">
          Enter Custom Deposit:
        </label>
        <input
          id="customDeposit"
          type="number"
          min={0}
          className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
          value={customSecurityDeposit ?? ""}
          onChange={(e) => setCustomSecurityDeposit(Number(e.target.value))}
        />
      </div>
    )}
  </div>
)}


              </div>



              <div>
                <label className="block text-sm md:text-lg font-semibold mb-2 font-sans">
                  {t("Entry Type")}
                </label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setValue("entryType", "single")}
                    className={`w-full py-1 px-2  text-center ${watch("entryType") === "single"
                        ? "entry text-sm md:text-lg"
                        : "bg-gray-100 text-sm md:text-lg"
                      } font-sans`}
                  >
                    {t("Single")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setValue("entryType", "multiple")}
                    className={`w-full py-1 px-2  text-center ${watch("entryType") === "multiple"
                        ? "entry text-sm md:text-lg"
                        : "bg-gray-100 text-sm md:text-lg"
                      } font-sans`}
                  >
                    {t("Multiple")}
                  </button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm md:text-lg font-semibold mb-2 font-sans">
                  {t("Duration")}
                </label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setValue("duration", "30 days")}
                    className={`w-full py-1 px-2 text-center ${watch("duration") === "30 days"
                        ? "entry text-sm md:text-lg"
                        : "bg-gray-100 text-sm md:text-lg"
                      } font-sans`}
                  >
                    {t("30 days")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setValue("duration", "60 days")}
                    className={`w-full py-1 px-2 text-center ${watch("duration") === "60 days"
                        ? "entry text-sm md:text-lg"
                        : "bg-gray-100 text-sm md:text-lg"
                      } font-sans`}
                  >
                    {t("60 days")}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm md:text-lg font-semibold mb-2 font-sans">
                  {t("Process Time")}
                </label>
                <Controller
                  name="processTime"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      options={[
                        {
                          label: "Express - 1~2 working days",
                          value: "Express - 1~2 working days",
                        },
                        {
                          label: "Regular - 3-5 working days",
                          value: "Regular - 3-5 working days",
                        },
                      ]}
                      onChange={(e) => {
                        field.onChange(e.value);
                        calculateTotalPrice();
                      }}
                      placeholder={t("Select Process Time")}
                      className="w-full bg-gray-100 border-2 border-gray-300 h-9 focus:outline-none focus:ring-2 focus:ring-blue-500 custom-dropdown font-sans"
                    />
                  )}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm md:text-lg font-semibold mb-2 font-sans">
                  {t("Number of Adults")}
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const value = watch("adultsNumber");
                      if (value > 1) setValue("adultsNumber", value - 1);
                    }}
                    className="bg-gray-200 p-0.5 px-2 font-sans"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    {...register("adultsNumber", { valueAsNumber: true })}
                    className="w-16 h-8 text-center p-2 border-2 border-gray-300 rounded-md font-sans"
                    min={1}
                    max={9}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const value = watch("adultsNumber");
                      setValue("adultsNumber", value + 1);
                    }}
                    className="entry py-0.5 px-2 font-sans"
                  >
                    +
                  </button>
                </div>
                {errors.adultsNumber && (
                  <p className="text-red-500 text-sm mt-1 font-sans">
                    {errors.adultsNumber.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm md:text-lg font-semibold mb-2 font-sans">
                  {t("Number of Children")}
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const value = watch("childrenNumber");
                      if (value > 0) setValue("childrenNumber", value - 1);
                    }}
                    className="bg-gray-200 p-0.5 px-2 font-sans"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    {...register("childrenNumber", { valueAsNumber: true })}
                    className="w-16 h-8 text-center p-2 border-2 border-gray-300 rounded-md font-sans"
                    min={0}
                    max={9}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const value = watch("childrenNumber");
                      setValue("childrenNumber", value + 1);
                    }}
                    className="entry p-0.5 px-2 font-sans"
                  >
                    +
                  </button>
                </div>
                {errors.childrenNumber && (
                  <p className="text-red-500 text-sm mt-1 font-sans">
                    {errors.childrenNumber.message}
                  </p>
                )}
              </div>
            </div>

            <div className="text-medium md:text-lg font-semibold font-sans">
              <span>{t("Total Price")}: </span>
              <span className="text-sm text-red-600">{totalPrice} AED</span>
            </div>

            <div className="mt-6">
              <h3 className="text-sm md:text-xl font-semibold mb-4 text-gray-800 font-sans">
                {t("Required Documents")}
              </h3>
              <ul className="list-disc pl-6 space-y-2">
                {service.requiredFiles?.map((file, index) => (
                  <li
                    key={index}
                    className="text-small md:text-xl text-gray-700 flex items-center space-x-2 font-sans"
                  >
                    <span className="flex-shrink-0 text-blue-600">✔</span>
                    <span>{separateCamelCase(file)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex justify-center items-center">
              <button
                type="submit"
                className="entry px-3 md:px-6 py-1 font-sans"
              >
                {t("Book Now")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default DocumentBookingForm;